import React, { Component } from 'react';
import { connect } from 'react-redux';
import _times from 'lodash/times';
import _uniqId from 'lodash/uniqueId';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { Affix } from 'grab-ui';
import RouteOptimizationLocationDrawer from 'components/v2/route-optimization-location-drawer/route-optimization-location-drawer';
import OrderSummaryWidget from 'components/v2/order-summary-widget/order-summary-widget';
import BackButton from 'components/v2/back-button/back-button';
import BookOrderForm from 'views/v2/home/book-order-form';
import { SHOPIFY_MODE } from 'views/v2/shopify-home/constants';
import css from 'views/v2/home/bulk-order-page.less';
import {
  pushNotificationAction,
  showFailedPaymentsRetryModalAction,
  toggleLocationDrawerAction,
} from 'actions/app-actions';
import {
  userInfoSelector,
  isCashPaymentDisabledSelector,
  isCashlessPaymentDisabledSelector,
} from 'selectors/app-selector';
import { paymentMethodSelector } from 'selectors/booking-selectors';
import { shopifyMultipleOrdersSelector } from 'selectors/shopify-selectors';
import {
  updateShopifyMultipleOrdersAction,
  updateShopifyMultipleOrdersShallowAction,
  updateShopifyMsdOrderAction,
  updateShopifyMsdOrderShallowAction,
  createShopifyDeliveriesAction,
} from 'actions/shopify-actions';
import {
  toCreateShopifyMultipleDeliveries,
  toCreateShopifyMsdDeliveries,
} from 'helper/shopify';
import { moveExtraFields, setExtraFields } from 'helper/order';
import formatCurrency from 'common/utils/format-currency';
import { reorderByIndex } from 'helper/reorder';
import { SCRIBE_SHOPIFY_EVENTS } from 'applications/scribe/instrumentations/track';

import ShopifyMsdCta from './shopify-msd-cta/shopify-msd-cta';
import { toBookMSD } from '../shopify-orders/utils';
import ShopifyMsdSaving from './shopify-msd-saving/shopify-msd-saving';
import {
  scribeTrackClickBookOrder,
  scribeTrackClickGroupOrder,
  scribeTrackDeliveryDetailDefault,
  scribeTrackEditDelivery,
} from './shopify-create-booking-tracking';
import {
  getMsdCtaData,
  getMsdOrderSavingAmount,
} from '../common/utils/msd-utils';
import ShopifyTrackEventContext from '../track-event';

const orderFormViewIdPrefix = 'orderForm#';
class ShopifyCreateBooking extends Component {
  static contextType = ShopifyTrackEventContext;
  constructor(props) {
    super(props);
    this.state = {
      locationDrawerID: _uniqId('drawer'),
      focusedOrderIndex: 0,
      isLoading: false,
    };
  }

  componentDidMount() {
    const { mode, msdOrder, multipleOrders } = this.props;
    scribeTrackDeliveryDetailDefault({
      ctx: this.context,
      eventName: SCRIBE_SHOPIFY_EVENTS.DEFAULT,
      mode,
      msdOrder,
      multipleOrders,
    });
  }

  scrollToOrder = index => {
    setTimeout(() => {
      const targetOrderFormElement = document.getElementById(
        `${orderFormViewIdPrefix}${index}`,
      );
      if (targetOrderFormElement) {
        targetOrderFormElement.scrollIntoView();
      }
    });
  };

  onUpdateDropoffLocations = (locations, oldIndexes) => {
    const { msdOrder, updateShopifyMsdOrder } = this.props;
    let newOrder = {
      ...msdOrder,
      recipientLocations: locations,
    };
    newOrder = moveExtraFields(newOrder, oldIndexes);
    setExtraFields(newOrder);
    newOrder.fulfillmentOrders = reorderByIndex(
      newOrder.fulfillmentOrders,
      oldIndexes,
    );
    updateShopifyMsdOrder({ order: newOrder });
  };

  onEditAllDropoffLocations = () => {
    const { msdOrder, toggleLocationDrawer } = this.props;
    this.setState({
      locationDrawerID: _uniqId('drawer'),
    });
    toggleLocationDrawer(msdOrder.recipientLocations, false, true);
    scribeTrackEditDelivery({ ctx: this.context, msdOrder });
  };

  onClickOrderNumber = index => {
    this.setState({ focusedOrderIndex: index }, () => {
      this.scrollToOrder(index);
    });
  };

  onUpdateOrder = payload => {
    const {
      mode,
      updateShopifyMultipleOrders,
      updateShopifyMsdOrder,
    } = this.props;
    if (mode === SHOPIFY_MODE.SHOPIFY_BOOK_MULTIPLE) {
      updateShopifyMultipleOrders(payload);
    } else if (mode === SHOPIFY_MODE.SHOPIFY_BOOK_MSD) {
      updateShopifyMsdOrder(payload);
    }
  };

  onUpdateOrderShallow = payload => {
    const {
      mode,
      updateShopifyMultipleOrdersShallow,
      updateShopifyMsdOrderShallow,
    } = this.props;
    if (mode === SHOPIFY_MODE.SHOPIFY_BOOK_MULTIPLE) {
      updateShopifyMultipleOrdersShallow(payload);
    } else if (mode === SHOPIFY_MODE.SHOPIFY_BOOK_MSD) {
      updateShopifyMsdOrderShallow(payload);
    }
  };

  onRemoveOrder = index => () => {
    const { multipleOrders, updateShopifyMultipleOrdersShallow } = this.props;
    const newOrders = multipleOrders.filter(
      (_order, currentIndex) => currentIndex !== index,
    );
    updateShopifyMultipleOrdersShallow({
      multipleOrders: newOrders,
    });
  };

  onClickPlaceOrder = () => {
    this.setState({ isLoading: true }, () => {
      this.createDelivery();
    });
  };

  createDelivery = () => {
    const {
      multipleOrders,
      msdOrder,
      createShopifyDeliveries,
      userInfo,
      payments,
      mode,
      onBookingCreateComplete,
    } = this.props;
    const orderToPlace =
      mode == SHOPIFY_MODE.SHOPIFY_BOOK_MULTIPLE
        ? toCreateShopifyMultipleDeliveries(multipleOrders, userInfo, payments)
        : toCreateShopifyMsdDeliveries(msdOrder, userInfo, payments);

    createShopifyDeliveries(orderToPlace, onBookingCreateComplete).finally(
      () => {
        this.setState({ isLoading: false });
        const { msdOrder, multipleOrders, mode } = this.props;
        scribeTrackClickBookOrder({
          ctx: this.context,
          msdOrder,
          multipleOrders,
          mode,
          userInfo,
        });
      },
    );
  };

  renderBookOrderForms = () => {
    const {
      mode,
      multipleOrders,
      msdOrder,
      payments,
      userInfo,
      isCashPaymentDisabled,
      isCashlessPaymentDisabled,
      pushNotification,
      showFailedPaymentsRetryModal,
      optimizeQuotesStatus,
    } = this.props;
    const isMSD = mode === SHOPIFY_MODE.SHOPIFY_BOOK_MSD;
    const orders = isMSD ? [msdOrder] : multipleOrders;

    return _times(orders.length, index => {
      const order = orders[index];

      return (
        <div id={`${mode}#${index}`} key={`${mode}#${index}`}>
          <BookOrderForm
            enableRouteOptimization={isMSD}
            index={index}
            mode={mode}
            order={order}
            orders={orders}
            payments={payments}
            userInfo={userInfo}
            allowCopy={false}
            isCashPaymentDisabled={isCashPaymentDisabled}
            isCashlessPaymentDisabled={isCashlessPaymentDisabled}
            pushNotification={pushNotification}
            onRetryFailedPayments={showFailedPaymentsRetryModal}
            updateBulkOrder={this.onUpdateOrder}
            updateBulkOrderShallow={this.onUpdateOrderShallow}
            onDeleteBulkForm={this.onRemoveOrder(index)}
            onEditAllDropoffLocations={this.onEditAllDropoffLocations}
            optimizeQuotesStatus={optimizeQuotesStatus}
          />
        </div>
      );
    });
  };

  renderMsdSaving = () => {
    const { mode, msdOrder } = this.props;
    const { err, savingsAmount, currency } = getMsdOrderSavingAmount({
      mode,
      msdOrder,
    });

    if (err) {
      return null;
    }

    return (
      <ShopifyMsdSaving
        savingsAmount={formatCurrency({
          code: currency.code,
          symbol: currency.symbol,
          amount: savingsAmount,
          hideSymbol: false,
        })}
      />
    );
  };

  renderMsdCta = () => {
    const { onChangeToMSD, multipleOrders, mode } = this.props;
    const {
      err,
      validQuotes,
      totalQuoteAmount,
      totalSavings,
      msdInfo,
    } = getMsdCtaData({ mode, multipleOrders });

    if (err) {
      return null;
    }

    const { currency } = validQuotes[0];

    return (
      <ShopifyMsdCta
        savingsAmount={formatCurrency({
          code: currency.code,
          symbol: currency.symbol,
          amount: totalSavings,
          hideSymbol: false,
        })}
        onGroupOrder={() => {
          onChangeToMSD(
            toBookMSD({
              ...msdInfo,
              singleBookingsTotalQuote: totalQuoteAmount,
            }),
          );

          scribeTrackClickGroupOrder({
            ctx: this.context,
            multipleOrders,
            mode,
          });
        }}
      />
    );
  };

  render() {
    const { focusedOrderIndex, locationDrawerID, isLoading } = this.state;
    const {
      onClickBack,
      multipleOrders,
      userInfo,
      mode,
      msdOrder,
    } = this.props;
    return (
      <div>
        <BackButton onClick={onClickBack} />
        <h4 className={css.pageHeader}>
          {I18n.t(
            `shopify.orders.createBookingPage.${
              mode === SHOPIFY_MODE.SHOPIFY_BOOK_MSD
                ? 'MSD'
                : 'multipleBookings'
            }.title`,
          )}
        </h4>
        <div className={css.orderPanel}>
          {mode === SHOPIFY_MODE.SHOPIFY_BOOK_MULTIPLE && (
            <div className={css.hangingOnLeft}>
              <Affix offset={24}>
                <div className={css.orderQtyContainer}>
                  <div className={css.orderQty}>
                    <div className={css.numbers}>
                      {_times(multipleOrders.length, index => (
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                        <h6
                          className={
                            index === focusedOrderIndex
                              ? css.focusedNumberText
                              : null
                          }
                          key={`number-${index}`}
                          onClick={() => this.onClickOrderNumber(index)}
                          onKeyDown={null}
                        >
                          {index + 1}
                        </h6>
                      ))}
                    </div>
                  </div>
                </div>
              </Affix>
            </div>
          )}
          <div className={css.orderForms}>{this.renderBookOrderForms()}</div>
          <OrderSummaryWidget
            isForBulk={mode === SHOPIFY_MODE.SHOPIFY_BOOK_MULTIPLE}
            orders={multipleOrders}
            order={msdOrder}
            onClickPlaceOrder={this.onClickPlaceOrder}
            userInfo={userInfo}
            msdCta={this.renderMsdCta()}
            msdSaving={this.renderMsdSaving()}
            isLoading={isLoading}
          />
        </div>
        <RouteOptimizationLocationDrawer
          key={locationDrawerID}
          order={msdOrder}
          onUpdate={this.onUpdateDropoffLocations}
          allowMultipleAddresses
          isShopifyMSD
        />
      </div>
    );
  }
}

ShopifyCreateBooking.propTypes = {
  mode: PropTypes.string.isRequired,
  multipleOrders: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  msdOrder: PropTypes.shape({}).isRequired,
  payments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  userInfo: PropTypes.shape({}).isRequired,
  onClickBack: PropTypes.func.isRequired,
  isCashPaymentDisabled: PropTypes.bool.isRequired,
  isCashlessPaymentDisabled: PropTypes.bool.isRequired,
  pushNotification: PropTypes.func.isRequired,
  showFailedPaymentsRetryModal: PropTypes.func.isRequired,
  updateShopifyMultipleOrders: PropTypes.func.isRequired,
  updateShopifyMultipleOrdersShallow: PropTypes.func.isRequired,
  updateShopifyMsdOrder: PropTypes.func.isRequired,
  updateShopifyMsdOrderShallow: PropTypes.func.isRequired,
  createShopifyDeliveries: PropTypes.func.isRequired,
  onBookingCreateComplete: PropTypes.func.isRequired,
  onChangeToMSD: PropTypes.func.isRequired,
  toggleLocationDrawer: PropTypes.func.isRequired,
  optimizeQuotesStatus: PropTypes.shape({}),
};

ShopifyCreateBooking.defaultProps = {
  optimizeQuotesStatus: {},
};

const mapStateToProps = state => ({
  multipleOrders: shopifyMultipleOrdersSelector(state),
  msdOrder: state.shopify.msdOrder,
  payments: paymentMethodSelector(state),
  userInfo: userInfoSelector(state),
  isCashPaymentDisabled: isCashPaymentDisabledSelector(state),
  isCashlessPaymentDisabled: isCashlessPaymentDisabledSelector(state),
  optimizeQuotesStatus: state.shopify.msdOrder.optimizeQuotesStatus,
});

const mapDispatchToProps = {
  pushNotification: pushNotificationAction,
  showFailedPaymentsRetryModal: showFailedPaymentsRetryModalAction,
  updateShopifyMultipleOrders: updateShopifyMultipleOrdersAction,
  updateShopifyMultipleOrdersShallow: updateShopifyMultipleOrdersShallowAction,
  updateShopifyMsdOrder: updateShopifyMsdOrderAction,
  updateShopifyMsdOrderShallow: updateShopifyMsdOrderShallowAction,
  createShopifyDeliveries: createShopifyDeliveriesAction,
  toggleLocationDrawer: toggleLocationDrawerAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(
  ShopifyCreateBooking,
);
